import { isNull, uniq } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { OpenFieldSection, SearchFieldSection } from '../../../datasets/instances/store/types';
import { getAvailableDocumentNames } from '../../../admin/documents/store/selectors';
import { DatasetDefinition, DatasetType, DefinitionHierarchy, DocumentSpecificHiddenFields, HiddenFields } from '../../../datasets/store/types';
import { SearchFilter } from '../../search/store/types';
import { getDocumentReportingRoot } from '../../store/selectors';
import { AgreementTypeReportFields, DocumentMetaReportField, DocumentReportField, DocumentReportTemplate, ReportPage, ReportTab, UpsertDocumentReportTemplate } from './types';

export const getReportPage = (state: ApplicationState): ReportPage => getDocumentReportingRoot(state).reportPage;

export const getSelectedTab = (state: ApplicationState): ReportTab => getDocumentReportingRoot(state).selectedTab;

export const getReportFilters = (state: ApplicationState): SearchFilter => getDocumentReportingRoot(state).reportFilters;

export const getSelectedDocumentNameIds = (state: ApplicationState): string[] => {
    return getReportFilters(state).documentNameId || [];
};

export const getSelectedAgreementTypeId = (state: ApplicationState): number | null => getDocumentReportingRoot(state).selectedAgreementTypeId;

export const getReportAgreementTypeIds = (state: ApplicationState): number[] => {
    const allReportFields = getAllReportFields(state);
    return Object.keys(allReportFields).map(agreementTypeIdString => parseInt(agreementTypeIdString));
};

export const getRelevantDocumentNameIds = (agreementTypeId: number) => (state: ApplicationState): string[] => {
    const reportDocumentNameIds = getSelectedDocumentNameIds(state);
    const allDocumentNames = getAvailableDocumentNames(state);
    const agreementTypeDocumentNameIds = allDocumentNames.filter(documentName => documentName.agreementTypeId === agreementTypeId).map(({ documentNameId }) => documentNameId!.toString());
    const relevantDocumentNameIds = reportDocumentNameIds.filter(documentNameId => agreementTypeDocumentNameIds.includes(documentNameId));
    return relevantDocumentNameIds.length > 0 ? relevantDocumentNameIds : agreementTypeDocumentNameIds;
};

export const getUpdatedAgreementTypeDocumentNameIds = (updatedDocumentNameIds: string[], agreementTypeId: number) => (state: ApplicationState): string[] => {
    const allDocumentNames = getAvailableDocumentNames(state);
    const agreementTypeDocumentNameIds = allDocumentNames.filter(documentName => documentName.agreementTypeId === agreementTypeId).map(({ documentNameId }) => documentNameId!.toString());
    const relevantDocumentNameIds = updatedDocumentNameIds.filter(documentNameId => agreementTypeDocumentNameIds.includes(documentNameId));
    return relevantDocumentNameIds;
};

export const getUpdatedDocumentNameIds = (updatedAgreementTypeIds: string[] | null) => (state: ApplicationState): string[] | null => {
    if (isNull(updatedAgreementTypeIds)) {
        return null;
    }
    const reportAgreementTypeIds = getReportAgreementTypeIds(state);
    const addedAgreementTypeIds = updatedAgreementTypeIds.filter(updatedAgreementTypeId => !reportAgreementTypeIds.includes(parseInt(updatedAgreementTypeId)));
    const removedAgreementTypeIds = reportAgreementTypeIds.filter(existingAgreementTypeId => !updatedAgreementTypeIds.includes(existingAgreementTypeId.toString()));
    const reportDocumentNameIds = getSelectedDocumentNameIds(state);
    const allDocumentNames = getAvailableDocumentNames(state);
    const addedAgreementTypeDocumentNameIds = allDocumentNames.filter(({ agreementTypeId }) => !isNull(agreementTypeId) && addedAgreementTypeIds.includes(agreementTypeId.toString())).map(({ documentNameId }) => documentNameId!.toString());
    const existingDocumentNameIds = allDocumentNames.filter(({ documentNameId }) => reportDocumentNameIds.includes(documentNameId!.toString())).filter(({ agreementTypeId }) => !removedAgreementTypeIds.includes(agreementTypeId!)).map(({ documentNameId }) => documentNameId!.toString());
    return uniq([...addedAgreementTypeDocumentNameIds, ...existingDocumentNameIds]);
};

export const getAllReportFields = (state: ApplicationState): AgreementTypeReportFields => getDocumentReportingRoot(state).reportFields;

export const getReportFields = (state: ApplicationState): DocumentReportField[] => {
    const allReportFields = getAllReportFields(state);
    const selectedAgreementTypeId = getSelectedAgreementTypeId(state);
    return !isNull(selectedAgreementTypeId) && allReportFields[`${selectedAgreementTypeId}`] || [];
};

export const getReportFieldsTableDatasetId = (state: ApplicationState): number | null => {
    const reportFields = getReportFields(state);
    return reportFields.find(({ datasetType }) => datasetType === DatasetType.TABLE)?.datasetId || null;
};

export const getIsLoading = (state: ApplicationState): boolean => getDocumentReportingRoot(state).fetchingAvailableFields;

export const getIsGeneratingReport = (state: ApplicationState): boolean => getDocumentReportingRoot(state).generatingReport;

export const getAvailableFieldsError = (state: ApplicationState): null | string => getDocumentReportingRoot(state).fetchingError;

export const getBackConfirmationModalOpen = (state: ApplicationState): boolean => getDocumentReportingRoot(state).backConfirmationModalOpen;

export const getReportUpdated = (state: ApplicationState): boolean => getDocumentReportingRoot(state).reportUpdated;

export const getAllReportTemplates = (state: ApplicationState): DocumentReportTemplate[] => getDocumentReportingRoot(state).reportTemplates;

export const getSaveTemplateModalOpen = (state: ApplicationState): boolean => getDocumentReportingRoot(state).saveTemplateModalOpen;

export const getReportTemplateName = (state: ApplicationState): string => getDocumentReportingRoot(state).templateName;
export const getReportTemplateDescription = (state: ApplicationState): string => getDocumentReportingRoot(state).templateDescription;

export const getIsFetchingTemplates = (state: ApplicationState): boolean => getDocumentReportingRoot(state).fetchingTemplates;

export const getSelectedTemplate = (state: ApplicationState): DocumentReportTemplate | null => getDocumentReportingRoot(state).selectedTemplate;

export const getSelectedTemplateId = (state: ApplicationState): number | undefined => getSelectedTemplate(state)?.reportTemplateId;

export const getUpsertReportTemplate = (state: ApplicationState): UpsertDocumentReportTemplate => {
    const reportFields = getAllReportFields(state);
    const name: string = getReportTemplateName(state);
    const description: string = getReportTemplateDescription(state);
    const reportTemplateId = getSelectedTemplateId(state);
    const filters = getReportFilters(state);
    const metaFields = getReportMetaFields(state);
    return {
        name,
        description,
        reportTemplateId,
        reportFields,
        filters,
        isOpinionReport: false,
        metaFields
    };
};

export const getConfirmDeleteReportTemplate = (state: ApplicationState): number | null => getDocumentReportingRoot(state).confirmDeleteReportTemplate;

export const getConfirmDeleteModalOpen = (state: ApplicationState): boolean => !isNull(getConfirmDeleteReportTemplate(state));

export const getIsDeleting = (state: ApplicationState): boolean => getDocumentReportingRoot(state).isDeleting;

export const getReportDatasetDefinitions = (state: ApplicationState): DatasetDefinition[] => getDefinitionHierarchy(state).map(({ definition }) => definition);

export const getReportOpenFieldSections = (state: ApplicationState): OpenFieldSection[] => getDocumentReportingRoot(state).openFieldsAndSections;

export const getParentDatasetId = (state: ApplicationState): number | null => {
    const selectedAgreementTypeId = getSelectedAgreementTypeId(state);
    if (!isNull(selectedAgreementTypeId)) {
        const documentNames = getAvailableDocumentNames(state);
        const parentDatasetId = documentNames.find(({ agreementTypeId }) => agreementTypeId === selectedAgreementTypeId)?.datasetId || null;
        return parentDatasetId;
    }
    return null;
};

export const getClientHiddenFields = (state: ApplicationState): HiddenFields => getDocumentReportingRoot(state).clientHiddenFields;

export const getDocumentSpecificHiddenFields = (state: ApplicationState): DocumentSpecificHiddenFields => getDocumentReportingRoot(state).documentHiddenFields;

export const getDefinitionHierarchy = (state: ApplicationState): DefinitionHierarchy[] => getDocumentReportingRoot(state).definitionHierarchy;

export const getAllSectionsAndFields = (state: ApplicationState): SearchFieldSection[] => getDocumentReportingRoot(state).allSearchFieldSections;

export const getFieldSectionSearchValue = (state: ApplicationState): string => getDocumentReportingRoot(state).fieldSectionSearch;

export const getSearchFuzzyMatches = (state: ApplicationState): SearchFieldSection[] => getDocumentReportingRoot(state).searchFuzzyMatches;

export const getSearchFieldSection = (state: ApplicationState): SearchFieldSection | null => getDocumentReportingRoot(state).searchFieldSection;

export const getReportMetaFields = (state: ApplicationState): DocumentMetaReportField[] => getDocumentReportingRoot(state).metaFields;
